import styled, { css, keyframes } from "styled-components";
import { IconLock as IconLockSvg } from "assets/icons/index";

// Animations
const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const confettiFall = keyframes`
  0% {
    transform: translateY(-20px) translateX(0) rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: translateY(100px) translateX(var(--tx)) rotate(720deg);
    opacity: 0;
  }
`;

// Styled Components
export const Card = styled.div`
  width: 100%;
  padding: 20px 24px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e9eaeb;
  display: flex;
  flex-direction: column;
  gap: 24px;
  font-family: "Geist", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto",
    sans-serif;

  @media (max-width: 768px) {
    padding: 16px 20px;
    gap: 20px;
  }
`;

export const BackButton = styled.button`
  background: none;
  border: none;
  color: #535862;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: color 0.2s;
  font-family: "Geist", sans-serif;

  &:hover {
    color: #181d27;
  }
`;

export const BackIcon = styled.span`
  font-size: 16px;
`;

export const StrategyLogo = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 9999px;
  object-fit: cover;
`;

export const StrategyInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const TabsContainer = styled.div`
  display: flex;
  gap: 24px;
  margin: -4px 0 0 0;
`;

export const Tab = styled.button`
  background: none;
  border: none;
  padding: 0 0 12px 0;
  font-size: 18px;
  font-weight: 500;
  color: ${(props) => (props.$active ? "#1841dd" : "#858D9D")};
  cursor: pointer;
  border-bottom: 2px solid
    ${(props) => (props.$active ? "#1841dd" : "transparent")};
  transition: all 0.2s;
  position: relative;
  font-family: "Aeonik", sans-serif;
  line-height: 28px;
  margin-bottom: -2px;

  &:hover {
    color: ${(props) => (props.$active ? "#1841dd" : "#535862")};
  }
`;

export const FormContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const DropdownWrapper = styled.div`
  position: relative;
`;

export const DropdownButton = styled.button`
  width: 100%;
  padding: 8px 12px;
  background: #ffffff;
  border: 1px solid #d5d7da;
  border-radius: 8px;
  font-size: 16px;
  color: #181d27;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  font-family: "Geist", sans-serif;
  line-height: 24px;
  box-shadow: 0 0 0 1px rgba(10, 13, 18, 0.18) inset,
    0 -2px 0 0 rgba(10, 13, 18, 0.05) inset, 0 1px 2px 0 rgba(10, 13, 18, 0.05);

  &:hover {
    border-color: #535862;
  }

  span:first-of-type {
    flex: 1;
    text-align: left;
  }
`;

export const DropdownPlaceholder = styled.span`
  color: #535862;
  flex: 1;
  text-align: left;
`;

export const DropdownIcon = styled.span`
  font-size: 10px;
  color: #858d9d;
  transition: transform 0.2s;
  margin-left: auto;
`;

export const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #ffffff;
  border: 1px solid #d5d7da;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(10, 13, 18, 0.1);
  z-index: 10;
  overflow: hidden;
`;

export const DropdownItem = styled.button`
  width: 100%;
  padding: 10px 12px;
  background: none;
  border: none;
  font-size: 16px;
  color: #181d27;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.2s;
  text-align: left;
  font-family: "Geist", sans-serif;

  &:hover {
    background-color: #f3f4f6;
  }
`;

export const RobinhoodBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: #c4f000;
  border-radius: 4px;
  font-size: 12px;
  flex-shrink: 0;
`;

export const AvailableCashRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0;
  font-size: 14px;
`;

export const AmountInputContainer = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid #d5d7da;
  border-radius: 8px;
  padding: 8px 12px;
  gap: 8px;
  transition: all 0.2s;
  background: #ffffff;
  box-shadow: 0 0 0 1px rgba(10, 13, 18, 0.18) inset,
    0 -2px 0 0 rgba(10, 13, 18, 0.05) inset, 0 1px 2px 0 rgba(10, 13, 18, 0.05);

  &:focus-within {
    border-color: #1841dd;
    box-shadow: 0 0 0 1px #1841dd inset, 0 -2px 0 0 rgba(10, 13, 18, 0.05) inset,
      0 1px 2px 0 rgba(10, 13, 18, 0.05);
  }
`;

export const CurrencySymbol = styled.span`
  color: #535862;
  font-size: 16px;
  font-family: "Geist", sans-serif;
`;

export const AmountInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 16px;
  color: #181d27;
  padding: 0;
  font-family: "Geist", sans-serif;
  line-height: 24px;
  background: transparent;

  &::placeholder {
    color: #858d9d;
  }
`;

export const CurrencyCode = styled.span`
  color: #535862;
  font-size: 16px;
  font-weight: 400;
  font-family: "Geist", sans-serif;
`;

export const Divider = styled.div`
  height: 1px;
  background: #e9eaeb;
  margin: 8px 0;
`;

export const AlertBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  background: #f0f5ff;
  border-radius: 8px;
  border: 1px solid #d6e4ff;
`;

export const AlertIcon = styled.span`
  font-size: 18px;
  flex-shrink: 0;
  line-height: 18px;
`;

export const AlertText = styled.span`
  font-size: 12px;
  color: #1841dd;
  line-height: 18px;
  font-family: "Geist", sans-serif;
  font-weight: 500;

  strong {
    font-weight: 600;
  }
`;

export const Button = styled.button`
  width: 100%;
  padding: 10px 16px;
  background: ${(props) => (props.$disabled ? "#f3f4f6" : "#ffffff")};
  color: ${(props) => (props.$disabled ? "#858d9d" : "#181d27")};
  border: 1px solid #d5d7da;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-family: "Geist", sans-serif;
  line-height: 24px;
  box-shadow: ${(props) =>
    props.$disabled
      ? "none"
      : "0 0 0 1px rgba(10, 13, 18, 0.18) inset, 0 -2px 0 0 rgba(10, 13, 18, 0.05) inset, 0 1px 2px 0 rgba(10, 13, 18, 0.05)"};

  &:hover:not(:disabled) {
    background: ${(props) => (props.$disabled ? "#f3f4f6" : "#f9fafb")};
    border-color: #858d9d;
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }
`;

export const Spinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid #d5d7da;
  border-top-color: #1841dd;
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
`;

export const ViewDetailsButton = styled.button`
  width: 100%;
  padding: 10px 16px;
  background: none;
  border: none;
  color: #535862;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  font-family: "Geist", sans-serif;
  line-height: 24px;
`;

export const SummaryTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #181d27;
  margin: 0;
  font-family: "Geist", sans-serif;
  line-height: 30px;
`;

export const SummarySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

export const SummaryLabel = styled.span`
  font-size: 14px;
  color: #535862;
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Geist", sans-serif;
  line-height: 20px;
`;

export const SummaryValue = styled.span`
  font-size: 14px;
  color: #181d27;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: ${(props) => (props.$amount ? "500" : "400")};
  font-family: "Geist", sans-serif;
  line-height: 20px;
  text-align: right;
`;

export const EstimatedCostBadge = styled.span`
  background: #f3f0ff;
  color: #6938ef;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: "Geist", sans-serif;
`;

export const OrderSubmittedHeader = styled.div`
  position: relative;
  padding-bottom: 8px;
`;

export const OrderSubmittedTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #181d27;
  margin: 0 0 4px 0;
  font-family: "Geist", sans-serif;
  line-height: 30px;
`;

export const ConfettiContainer = styled.div`
  position: absolute;
  top: -10px;
  right: -10px;
  width: 150px;
  height: 150px;
  pointer-events: none;
  overflow: visible;

  &::before,
  &::after {
    content: "";
    position: absolute;
    width: 8px;
    height: 8px;
    border-radius: 2px;
    animation: ${confettiFall} 2.5s ease-out infinite;
  }

  &::before {
    --tx: 20px;
    left: 20%;
    background: #4285f4;
    animation-delay: 0s;
  }

  &::after {
    --tx: -15px;
    left: 40%;
    background: #34a853;
    animation-delay: 0.3s;
  }

  ${[...Array(12)].map(
    (_, i) => `
    &::${i % 2 === 0 ? "before" : "after"} {
      --tx: ${(Math.random() - 0.5) * 40}px;
      left: ${10 + i * 8}%;
      background: ${["#4285f4", "#34a853", "#fbbc04", "#ea4335"][i % 4]};
      animation-delay: ${(i * 0.15).toFixed(2)}s;
    }
  `
  )}
`;

export const FormGroupRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  ${({ $mt }) => $mt && `margin-top: ${$mt};`}
  ${({ $mb }) => $mb && `margin-bottom: ${$mb};`}

  /* $stacked: label on top, control full-width at every breakpoint. */
  ${({ $stacked }) =>
    $stacked &&
    css`
      flex-direction: column;
      align-items: stretch;
      gap: 8px;

      & > * {
        width: 100%;
      }
    `}

  /* Stack label and control below 2xl (1400px) to avoid shrink/overflow */
  @media (max-width: 1400px) {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;

    /* Ensure controls take full width when stacked */
    & > * {
      width: 100%;
    }
  }
`;

export const ControlContainer = styled.div`
  .custom-dropdown .dropdown-toggle {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-radius: 8px;
    background-color: #fff !important;
    padding: 10px 12px;
    flex-shrink: 0;
    border-radius: 8px;
    border: 1px solid #d5d7da;
    font-size: 14px;
    font-weight: 500;
    font-family: "Geist", sans-serif;
    line-height: 20px;
    color: red;
    color: #717680;
  }

  .brokder-info-wrapper {
    display: flex;
    align-items: center;
    gap: 6px;

    img {
      width: 20px;
      height: 20px;
      border-radius: 50%;
    }

    .broker-name {
      color: #414651;
    }
    .placeholder-text {
      color: green !important;
    }
  }

  .dropdown-arrow {
    transition: transform 0.3s ease;
  }

  .dropdown-arrow.rotated {
    transform: rotate(180deg);
  }

  .custom-dropdown .dropdown-toggle::after {
    display: none;
  }

  .custom-dropdown .custom-dropdown-menu {
    width: 320px;
    max-height: 360px !important;
    padding: 6px 6px !important;
    border-radius: 8px !important;
    border: 1px solid rgba(0, 0, 0, 0.08) !important;
    background: #fff !important;
    box-shadow: 0 12px 16px -4px rgba(10, 13, 18, 0.08),
      0 4px 6px -2px rgba(10, 13, 18, 0.03),
      0 2px 2px -1px rgba(10, 13, 18, 0.04) !important;
    overflow-y: auto !important;
    margin-top: 5px;

    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .custom-dropdown-item {
    display: flex;
    align-items: start;
    justify-content: space-between;
    border-radius: 6px;
    background: #fff;
    padding: 6px 8px;
    transition: background 0.2s ease;
    overflow: hidden;
  }

  .custom-dropdown-item:hover {
    background-color: #fafafa !important;
  }

  .custom-dropdown-item.active-item {
    background-color: #fafafa;
  }

  .custom-dropdown-item.disabled-item {
    opacity: 0.8;
    cursor: not-allowed !important;
    pointer-events: none;
  }

  .custom-dropdown-item.disabled-item .account-info {
    opacity: 0.8;
  }

  .custom-dropdown-item.disabled-item .account-info .account-name {
    color: #9ca3af;
  }

  .custom-dropdown-item.disabled-item .account-info .account-balance {
    color: #d1d5db;
  }

  .custom-dropdown-item.disabled-item .radio-indicator {
    border-color: #d1d5db;
    background-color: #f9fafb;
  }

  .custom-dropdown-item .form-check-input {
    margin-left: 10px;
  }

  .account-info {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1 1 auto;
    min-width: 0;

    img {
      width: 32px;
      height: 32px;
      border-radius: 50%;
    }
    .account-details {
      display: flex;
      flex-direction: column;
      min-width: 0;
    }
    .account-name {
      color: #181d27;
      font-family: "Public Sans", sans-serif;
      font-size: 14px;
      font-weight: 500;
      line-height: 20px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .account-balance {
      color: #535862;
      font-family: "Public Sans", sans-serif;
      font-size: 12px;
      font-weight: 400;
      line-height: 18px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .radio-indicator {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 1px solid #d5d7da;
    flex-shrink: 0;
    margin-top: 2px;
    align-self: start;
  }

  .radio-indicator.selected {
    border: 5px solid #2258f1;
  }

  @media (max-width: 1400px) {
    width: 100%;
  }
`;

export const CurrencyCodeBox = styled.span`
  padding: 4px 8px;
  border: 1px solid #d5d7da;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #535862;
  background: #f9fafb;
  font-family: "Geist", sans-serif;
`;

export const CelebrationCard = styled.div`
  width: 100%;
  padding: 20px 24px;
  background: ${(props) =>
    props.backgroundImage ? `url(${props.backgroundImage})` : "#ffffff"};
  background-position: top;
  background-repeat: no-repeat;
  border-radius: 12px;
  border: 1px solid #e9eaeb;
  display: flex;
  flex-direction: column;
  gap: 24px;
  font-family: "Geist", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto",
    sans-serif;
  position: relative;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 16px 20px;
    gap: 20px;
  }
`;

export const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// Lock icon for the Next button (avoid inline styles)
export const LockIcon = styled(IconLockSvg)`
  width: 16px;
  height: 16px;
  margin-right: 8px;
  flex-shrink: 0;
`;

export const InputHelperRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
  width: 100%;
`;

export const SellAllButton = styled.button`
  background: transparent;
  border: none;
  color: #1841dd;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: "Geist", sans-serif;
  transition: color 0.2s;

  &:hover {
    color: #0f2ba0;
    text-decoration: underline;
  }
`;
